/**
 * 进阶决策引擎（§12 / §29.2）。
 *
 * 按规格伪代码实现，并补齐 §29.2 末尾要求的所有分支：
 *   - 组数减少后的比较（目标组数未完成 → 数据不完整，不进入普通判定）；
 *   - 用户手动改重（大档位跳跃按试重处理）；
 *   - 器械档位未知（defaultIncrementKg 缺失时按口径默认档位，文案提示核对器械刻度）；
 *   - 替换动作独立历史（判据以 exerciseId + exerciseVersion + 口径为准，天然独立）；
 *   - 不同重量口径（历史过滤只取同口径，绝不跨口径比较）；
 *   - 轻训练不计失败（只有 planType == normal 的历史才计入连续失败）；
 *   - 时间不足/提早结束（endedEarly/skipped → 数据不完整，不计失败）；
 *   - 休息过短（restShort → 维持）；
 *   - 新器械第一次记录（无历史 → 维持校准）。
 *
 * 输出 ProgressionVerdict（纯规则产物），由 toProgressionDecision 补齐持久化字段。
 * 关键安全铁律（ADR-006 / 决策 14.8）：
 *   - 永远允许如实保存 RIR 0；但禁止力竭动作出现 RIR 0 → UNPLANNED_FAILURE，不自动加重；
 *   - 关节疼痛/伤处不适 → recalibrate（撤销加重建议并重新评估），绝不机械加重。
 */

import type {
  Exercise,
  ProgressionDecision,
  ProgressionReasonCode,
  RirValue,
  WeightUnitScope,
} from "@jilian/shared";
import { RULES_VERSION } from "@jilian/shared";
import type { ProgressionInput, ProgressionVerdict, WorkoutExerciseResult } from "./interface";
import { reasonTextList } from "./reasons";

// ---------------------------------------------------------------------------
// 最小加重档位（§12 最小档位；defaultIncrementKg 优先，否则按口径默认）
// ---------------------------------------------------------------------------

const DEFAULT_INCREMENT_KG: Record<WeightUnitScope, number> = {
  per_dumbbell: 2.5, // 常见哑铃档
  total: 5, // 杠铃总重：每侧 2.5 → 总重 +5
  per_side: 2.5,
  machine_display: 5, // 器械常见一档（无 explicit 数据时保守取整档）
  bodyweight: 0, // 自重无加重档（靠次数推进）
  bodyweight_plus: 2.5,
  assistance: 5, // 辅助器械：数字越大越容易 → 进阶方向为「减辅助」
};

export function incrementKgFor(exercise: Exercise): number {
  if (exercise.defaultIncrementKg != null && exercise.defaultIncrementKg > 0) return exercise.defaultIncrementKg;
  const scope = scopeFromIncrement(exercise.incrementType);
  return DEFAULT_INCREMENT_KG[scope] ?? 0;
}

function scopeFromIncrement(incrementType: Exercise["incrementType"]): WeightUnitScope {
  switch (incrementType) {
    case "per_dumbbell":
      return "per_dumbbell";
    case "barbell_total":
      return "total";
    case "bodyweight":
      return "bodyweight";
    case "machine_stack":
    default:
      return "machine_display";
  }
}

/** 器械档位是否「未知」（无显式档位且为器械类）。 */
export function stackIncrementUnknown(exercise: Exercise): boolean {
  return (
    exercise.defaultIncrementKg == null &&
    (scopeFromIncrement(exercise.incrementType) === "machine_display" ||
      scopeFromIncrement(exercise.incrementType) === "assistance")
  );
}

// ---------------------------------------------------------------------------
// 历史可比性（同一趋势口径 + 正常训练日）
// ---------------------------------------------------------------------------

/** 历史是否与本次为「可比」：同动作、同器械版本、同重量口径、正常训练日、未跳过未早退。 */
export function isComparableHistory(
  historyItem: WorkoutExerciseResult,
  current: WorkoutExerciseResult,
): boolean {
  if (historyItem.exerciseId !== current.exerciseId) return false;
  if (historyItem.weightUnitScope !== current.weightUnitScope) return false;
  if (historyItem.skipped || historyItem.endedEarly) return false;
  // 轻训练/降量日不计入连续失败（§12.2 / §29.2 补充）
  if (historyItem.planType !== "normal") return false;
  return true;
}

function comparableHistory(history: WorkoutExerciseResult[], current: WorkoutExerciseResult): WorkoutExerciseResult[] {
  return history.filter((h) => isComparableHistory(h, current));
}

/** 最近一次可比较失败（任一工作组未达到最低次数）。 */
function lastComparableFailure(
  history: WorkoutExerciseResult[],
  current: WorkoutExerciseResult,
  repMin: number,
): WorkoutExerciseResult | null {
  const list = comparableHistory(history, current);
  for (let i = list.length - 1; i >= 0; i--) {
    const h = list[i]!;
    if (h.hasCompleteData && h.sets.some((s) => s.reps < repMin)) return h;
  }
  return null;
}

// ---------------------------------------------------------------------------
// 主判定
// ---------------------------------------------------------------------------

export function decideProgression(input: ProgressionInput): ProgressionVerdict {
  const { exercise, currentSession, history, readiness } = input;
  const scope = currentSession.weightUnitScope;

  const maintain = (reasonCodes: ProgressionReasonCode[]): ProgressionVerdict => ({
    decision: "maintain",
    previousWeightKg: currentSession.weightKg,
    suggestedWeightKg: null,
    weightUnitScope: scope,
    reasonCodes,
    humanReadableReason: reasonTextList(reasonCodes),
    ruleVersion: RULES_VERSION,
  });

  // 0) 数据不完整 / 无重量 → 维持（§29.2 INCOMPLETE_DATA）
  if (!currentSession.hasCompleteData || currentSession.weightKg == null || currentSession.weightKg <= 0) {
    return maintain(["INCOMPLETE_DATA"]);
  }

  // 1) 关节疼痛 / 伤处不适 → 撤销加重并重新评估（§12.2 / §17「次日伤处恶化撤销加重」）
  if (currentSession.jointPain || currentSession.injuryPain) {
    const reasonCodes: ProgressionReasonCode[] = [];
    if (currentSession.jointPain) reasonCodes.push("JOINT_PAIN");
    if (currentSession.injuryPain) reasonCodes.push("PAIN_REPORTED");
    return {
      decision: "recalibrate",
      previousWeightKg: currentSession.weightKg,
      suggestedWeightKg: null,
      weightUnitScope: scope,
      reasonCodes,
      humanReadableReason: reasonTextList(reasonCodes),
      ruleVersion: RULES_VERSION,
    };
  }

  // 2) 动作失控（formBreakdown）→ 维持
  if (currentSession.formBreakdown) {
    return maintain(["FORM_BREAKDOWN"]);
  }

  // 3) 休息过短（§12.2「实际休息明显短于建议时间」）
  if (currentSession.restShort) {
    return maintain(["SHORT_REST"]);
  }

  // 4) 跳过 / 提前结束 → 数据不完整，不计失败（§29.2 补充）
  if (currentSession.skipped || currentSession.endedEarly) {
    return maintain(["INCOMPLETE_DATA"]);
  }

  // 5) 降量/轻量/减量日 → 不自动加重（§12.2 条件 6 / §13；轻训练不计失败）
  if (!readiness.allowProgression || currentSession.planType !== "normal") {
    const reasonCodes: ProgressionReasonCode[] =
      currentSession.planType === "optional_light_day" ? ["LIGHT_DAY_SKIP_COUNT"] : ["REDUCED_DAY_NO_PROGRESSION"];
    return maintain(reasonCodes);
  }

  // 6) 禁止力竭动作记录 RIR 0 → UNPLANNED_FAILURE（决策 14.8）：不加重
  if (
    !exercise.failureAllowedLastSet &&
    currentSession.sets.some((s) => s.rir === 0)
  ) {
    return maintain(["UNPLANNED_FAILURE"]);
  }

  // 7) 大档位跳跃（用户手动改重造成增幅 >10% 的试重场景，§12.2「成功但档位跨度过大」）
  const prevComparable = comparableHistory(history, currentSession)[comparableHistory(history, currentSession).length - 1] ?? null;
  if (prevComparable && prevComparable.weightKg != null && prevComparable.weightKg > 0) {
    const userJump = (currentSession.weightKg - prevComparable.weightKg) / prevComparable.weightKg;
    if (userJump > 0.1 && currentSession.lastWeightManuallyChanged === true) {
      return {
        decision: "recalibrate",
        previousWeightKg: currentSession.weightKg,
        suggestedWeightKg: currentSession.weightKg,
        weightUnitScope: scope,
        reasonCodes: ["LARGE_INCREMENT_REQUIRES_TRIAL"],
        humanReadableReason: reasonTextList(["LARGE_INCREMENT_REQUIRES_TRIAL"]),
        ruleVersion: RULES_VERSION,
      };
    }
  }

  // 8) 全体达标判定
  const allReachedUpperBound = currentSession.sets.every((s) => s.reps >= exercise.repMax);
  const rirCompliant = currentSession.sets.every((s, index) => {
    const isLast = index === currentSession.sets.length - 1;
    const minimumRir = isLast && exercise.failureAllowedLastSet ? 0 : exercise.targetRir;
    return s.rir >= minimumRir;
  });
  const hasComparableHistory = comparableHistory(history, currentSession).length > 0;

  if (allReachedUpperBound && rirCompliant) {
    // 首次（无可比较历史）→ 视为校准（§12.3：首次执行进入校准，不自动加重）
    if (!hasComparableHistory) {
      return maintain(["NEW_MACHINE_CALIBRATION"]);
    }
    const inc = incrementKgFor(exercise);
    // 自重等无加重档的口径：不产出虚假的“加重同一重量”，维持并推进次数
    if (inc <= 0) {
      return maintain(["CONTINUE_DOUBLE_PROGRESSION"]);
    }
    const nextWeight = Math.max(0, Math.round((currentSession.weightKg + inc) * 10) / 10);
    const increaseRatio = (nextWeight - currentSession.weightKg) / currentSession.weightKg;

    if (increaseRatio > 0.1) {
      // §12.2：>10% 的大档位跳跃 → 试重（不强制加重）
      return {
        decision: "recalibrate",
        previousWeightKg: currentSession.weightKg,
        suggestedWeightKg: nextWeight,
        weightUnitScope: scope,
        reasonCodes: ["LARGE_INCREMENT_REQUIRES_TRIAL"],
        humanReadableReason: reasonTextList(["ALL_SETS_AT_UPPER_BOUND", "RIR_COMPLIANT", "LARGE_INCREMENT_REQUIRES_TRIAL"]),
        ruleVersion: RULES_VERSION,
      };
    }

    return {
      decision: "increase",
      previousWeightKg: currentSession.weightKg,
      suggestedWeightKg: nextWeight,
      weightUnitScope: scope,
      reasonCodes: ["ALL_SETS_AT_UPPER_BOUND", "RIR_COMPLIANT"],
      humanReadableReason: reasonTextList(["ALL_SETS_AT_UPPER_BOUND", "RIR_COMPLIANT"]),
      ruleVersion: RULES_VERSION,
    };
  }

  // 9) 低于下限 → 首次失败维持；两次可比较失败 → 降重
  const belowLowerBound = currentSession.sets.some((s) => s.reps < exercise.repMin);
  if (belowLowerBound) {
    if (!hasComparableHistory) {
      return maintain(["FIRST_FAILURE_MAINTAIN"]);
    }
    const previousFailure = lastComparableFailure(history, currentSession, exercise.repMin);
    if (previousFailure) {
      const inc = incrementKgFor(exercise);
      if (inc <= 0) {
        return maintain(["CONTINUE_DOUBLE_PROGRESSION"]);
      }
      const nextWeight = Math.max(0, Math.round((currentSession.weightKg - inc) * 10) / 10);
      return {
        decision: "decrease",
        previousWeightKg: currentSession.weightKg,
        suggestedWeightKg: nextWeight,
        weightUnitScope: scope,
        reasonCodes: ["TWO_CONSECUTIVE_COMPARABLE_FAILURES"],
        humanReadableReason: reasonTextList(["TWO_CONSECUTIVE_COMPARABLE_FAILURES"]),
        ruleVersion: RULES_VERSION,
      };
    }
    return maintain(["FIRST_FAILURE_MAINTAIN"]);
  }

  // 11) 其余：继续双进阶（维持并推进次数）
  return maintain(["CONTINUE_DOUBLE_PROGRESSION"]);
}

// ---------------------------------------------------------------------------
// Verdict → 持久化实体（保存「原建议 + 用户实际决定」）
// ---------------------------------------------------------------------------

export function toProgressionDecision(
  verdict: ProgressionVerdict,
  meta: {
    id: string;
    userId: string;
    exerciseId: string;
    sourceSessionId: string;
    createdAt: string;
    userAccepted?: boolean;
    userOverrideWeightKg?: number | null;
  },
): ProgressionDecision {
  return {
    id: meta.id,
    userId: meta.userId,
    exerciseId: meta.exerciseId,
    sourceSessionId: meta.sourceSessionId,
    decision: verdict.decision,
    previousWeightKg: verdict.previousWeightKg,
    suggestedWeightKg: verdict.suggestedWeightKg,
    weightUnitScope: verdict.weightUnitScope,
    reasonCodes: verdict.reasonCodes,
    humanReadableReason: verdict.humanReadableReason,
    ruleVersion: verdict.ruleVersion,
    userAccepted: meta.userAccepted ?? false,
    userOverrideWeightKg: meta.userOverrideWeightKg ?? null,
    createdAt: meta.createdAt,
  };
}

/** 测试辅助：判定 RIR 合规的最小 RIR（与 decideProgression 同源）。 */
export function minimumRirForSet(index: number, totalSets: number, exercise: Exercise): RirValue {
  const isLast = index === totalSets - 1;
  return (isLast && exercise.failureAllowedLastSet ? 0 : exercise.targetRir) as RirValue;
}